import {hasher} from 'hasher';

/** Split a hasher hash into path and query (no leading `#/`). */
export function splitHashPathAndQuery(hash: string): {path: string; search: string} {
    const i = hash.indexOf('?');
    if (i < 0) {
        return {path: hash, search: ''};
    }
    return {path: hash.slice(0, i), search: hash.slice(i + 1)};
}

/** Rejoin path + query for hasher.setHash / replaceHash. */
export function joinHashPathAndQuery(path: string, search: string): string {
    return search ? `${path}?${search}` : path;
}

/** Parse query params from a hasher hash or the current hash. */
export function getHashQueryParams(hash?: string): Record<string, string> {
    const raw = hash ?? hasher.getHash();
    const {search} = splitHashPathAndQuery(raw);
    if (!search) {
        return {};
    }
    const out: Record<string, string> = {};
    new URLSearchParams(search).forEach((value, key) => {
        out[key] = value;
    });
    return out;
}

/** True for 1 / true / yes (case-insensitive). Undefined when key missing. */
export function getHashQueryFlag(params: Record<string, string>, key: string): boolean | undefined {
    const raw = params[key];
    if (raw === undefined) {
        return undefined;
    }
    return /^(1|true|yes)$/i.test(raw);
}

/** Parse a numeric query value; undefined when missing or not finite. */
export function getHashQueryNumber(params: Record<string, string>, key: string): number | undefined {
    const raw = params[key];
    if (raw === undefined || raw === '') {
        return undefined;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

type THashQueryValue = string | number | boolean | null | undefined;

/** Append or replace the query string on a `#/…` or bare hash. Omits null/undefined/''. */
export function withHashQuery(
    hash: string | null | undefined,
    params: Record<string, THashQueryValue>,
): string | null {
    if (hash == null) {
        return null;
    }
    const qIndex = hash.indexOf('?');
    const path = qIndex < 0 ? hash : hash.slice(0, qIndex);
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === '') {
            continue;
        }
        if (typeof value === 'boolean') {
            qs.set(key, value ? '1' : '0');
        } else {
            qs.set(key, String(value));
        }
    }
    const search = qs.toString();
    return search ? `${path}?${search}` : path;
}
